import { Box, FormControl, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useCallback, useMemo, useState } from 'react';
import type { SummaryCreditRatingSlice } from '../api/chartDataTypes';
import { perf10kSelectSx } from '../styles/chartReader.styles';
import { LabelTypography, Perf10kSectionTitleTypography } from '../styles/chartReader.typography';

const menuPaperSx = {
    border: '1px solid #E4E4E4',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
};

const MENU_PROPS = { PaperProps: { sx: menuPaperSx } };

type SummaryCreditRatingSelectorProps = {
    /** Snapshot slices from the summary_credit_rating payload. Order is
     *  irrelevant — the selector sorts by `label` ascending internally. */
    slices: SummaryCreditRatingSlice[];
    /** Prefix for the `<select>` id so multiple snapshot selectors on a page
     *  never collide. */
    idPrefix: string;
    /** Fires when the user picks a credit rating. Selecting the same option
     *  again still fires so the parent can re-announce. */
    onSelectedSliceChange: (slice: SummaryCreditRatingSlice) => void;
};

/**
 * Single-dropdown selector for the Summary Credit Rating chart. Mirrors
 * CreditQualitySelector — same UX pattern, label set to "Data Series" per
 * spec.
 */
export function SummaryCreditRatingSelector({
    slices,
    idPrefix,
    onSelectedSliceChange,
}: SummaryCreditRatingSelectorProps) {
    const [selectedLabel, setSelectedLabel] = useState<string>('');

    const sortedSlices = useMemo(() => {
        return [...slices].sort((a, b) => a.label.localeCompare(b.label));
    }, [slices]);

    const handleChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            const nextLabel = event.target.value;
            setSelectedLabel(nextLabel);
            const match = sortedSlices.find((s) => s.label === nextLabel);
            if (match) onSelectedSliceChange(match);
        },
        [sortedSlices, onSelectedSliceChange],
    );

    const selectId = `${idPrefix}-slice-select`;

    return (
        <Box>
            <Perf10kSectionTitleTypography>Read a specific credit rating</Perf10kSectionTitleTypography>

            <Box sx={{ mt: 1.5 }}>
                <label htmlFor={selectId}>
                    <LabelTypography sx={{ mb: 0.5 }}>Data Series</LabelTypography>
                </label>
                <FormControl fullWidth size='small' sx={perf10kSelectSx}>
                    <Select<string>
                        id={selectId}
                        value={selectedLabel}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(v) => (v ? v : 'Select data series')}
                        inputProps={{ 'aria-label': selectedLabel === '' ? 'Select data series' : 'Data series' }}
                        MenuProps={MENU_PROPS}>
                        {sortedSlices.map((s) => (
                            <MenuItem key={s.label} value={s.label}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
}
